import { ClipboardContent } from 'src/app/general-components/form-controls/clipboard';
import { KrampShippingAddress } from '../kramp-shipping-addresses/kramp-shipping-address';
import {
  DeliveryAddress,
  KrampOrder,
  NseStatus,
  OrderShipmentInfo,
  PackageLine,
  ShipmentPackage,
  UnpackedLine,
} from './order-shipment-info';

export const NO_SHIPMENT_INFO = 'Nog geen verzendgegevens';
export const SHIP_TO_NO = 'Kramp Ship To No';
export const UNPACKED_LINES = 'Niet in een pakket';

const CARRIER_NAMES: Record<string, string> = {
  DHL: 'DHL',
  DHL_PARCEL: 'DHL',
  DHL_FREIGHT: 'DHL Freight',
  DSV: 'DSV',
  MAINFREIGHT: 'Mainfreight',
  NSE: 'NSE',
  PARTS_EXPRESS: 'Parts Express',
  POSTNL: 'PostNL',
  UPS: 'UPS',
};

/** NSE has no public track & trace page, so its status is looked up per package */
export const isNse = (shipmentPackage: ShipmentPackage): boolean =>
  shipmentPackage.carrier.trim().toUpperCase() === 'NSE';

/** The latest NSE scan as one line, like my-account shows it; null when NSE has no scans yet */
export const nseScanText = (nseStatus: NseStatus): string | null =>
  [nseStatus.dateTime, nseStatus.type, nseStatus.status].filter(Boolean).join(' - ') || null;

/** Latest NSE status per barcode, for the packages whose status has been looked up */
export type NseStatuses = ReadonlyMap<string, NseStatus>;

export const carrierName = (carrier: string): string => CARRIER_NAMES[carrier.toUpperCase()] ?? carrier;

// Freshdesk drops classes and <style> blocks on paste, so everything is inline
const FONT = 'font-family: Arial, Helvetica, sans-serif; font-size: 13px;';
const KEY_VALUE_TABLE = `border-collapse: collapse; ${FONT}`;
const KEY_CELL = 'padding: 2px 12px 2px 0; font-weight: bold; text-align: left; vertical-align: top;';
const VALUE_CELL = 'padding: 2px 0; text-align: left; vertical-align: top;';
const SPACER = 'margin: 0;';
const PARAGRAPH = `margin: 8px 0 4px 0; ${FONT}`;
const CONTENTS_TABLE = `border-collapse: collapse; ${FONT}`;
const CONTENTS_HEADER = 'border: 1px solid #cccccc; padding: 3px 8px; background: #f2f2f2; text-align: left;';
const CONTENTS_CELL = 'border: 1px solid #cccccc; padding: 3px 8px; text-align: left;';
const CONTENTS_CELL_NUMBER = 'border: 1px solid #cccccc; padding: 3px 8px; text-align: right;';

/** shipTo is the customer's Kramp ship-to address, or null when there is none (or it could not be looked up) */
export const buildAllCopy = (
  info: OrderShipmentInfo,
  shipTo: KrampShippingAddress | null,
  nseStatuses: NseStatuses = new Map()
): ClipboardContent => {
  const orderRows: Row[] = [
    ['Klantnummer', info.customerNumber],
    ['INDI ordernummer', info.orderNumber],
  ];
  if (!info.krampOrders.length) {
    return { plain: plainKeyValues(orderRows), html: htmlKeyValues(orderRows.map(escapedRow)) };
  }

  // The order rows open the first Kramp order's block, so the details read as one list without a gap
  const plain = info.krampOrders
    .map((krampOrder, index) => plainKrampOrder(index === 0 ? orderRows : [], krampOrder, shipTo, nseStatuses))
    .join('\n\n');

  const html = info.krampOrders
    .map((krampOrder, index) =>
      htmlKrampOrder(index === 0 ? orderRows.map(escapedRow) : [], krampOrder, shipTo, index > 0, nseStatuses)
    )
    .join('\n');

  return { plain, html };
};

export const buildPackageCopy = (
  info: OrderShipmentInfo,
  krampOrder: KrampOrder,
  shipmentPackage: ShipmentPackage,
  shipTo: KrampShippingAddress | null,
  nseStatuses: NseStatuses = new Map()
): ClipboardContent => {
  const rows: Row[] = [['INDI ordernummer', info.orderNumber], krampOrderNumberRow(krampOrder)];
  if (shipTo) {
    rows.push(shipToRow(krampOrder, shipTo));
  }
  const plain = [plainKeyValues(rows), plainPackage(shipmentPackage, nseStatuses)].join('\n\n');

  const html = [htmlKeyValues(rows.map(escapedRow)), htmlPackage(shipmentPackage, nseStatuses)].join('\n');

  return { plain, html };
};

export const buildShipToCopy = (shipTo: KrampShippingAddress): ClipboardContent => {
  const line = formatShipToLine(shipTo);
  return { plain: line, html: `<span style="${FONT}">${escapeHtml(line)}</span>` };
};

/** Compares postcode and house number, so differences in spacing, case or street spelling don't count */
export const shipToDiffersFrom = (deliveryAddress: DeliveryAddress | null, shipTo: KrampShippingAddress): boolean =>
  deliveryAddress !== null &&
  addressKey(deliveryAddress.address, deliveryAddress.postalCode) !== addressKey(shipTo.address, shipTo.zipCode);

/** The ship-to address as one line, like CS copy it from the Kramp verzendadressen page, minus the customer number shown above it */
export const formatShipToLine = (shipTo: KrampShippingAddress): string =>
  [shipTo.name, shipTo.comment, shipTo.address, shipTo.zipCode, shipTo.city, shipTo.shipToNo]
    .map(field => field?.trim())
    .filter(Boolean)
    .join(' ');

export const unpackedLineStatus = (line: UnpackedLine): string => {
  switch (line.status) {
    case 'DISPATCHED_WITHOUT_TRACKING': {
      const notes = line.deliveryNoteNumbers;
      if (!notes.length) {
        return 'Verzonden zonder track & trace';
      }
      return `Verzonden zonder track & trace (${notes.length === 1 ? 'pakbon' : 'pakbonnen'} ${notes.join(', ')})`;
    }
    case 'BACKORDERED':
      return 'In backorder';
    case 'NOT_DISPATCHED':
      return 'Nog niet verzonden';
  }
};

/** Nothing shipped and nothing known to be on its way */
export const hasNoShipmentInfo = (krampOrder: KrampOrder): boolean =>
  !krampOrder.packages.length && !krampOrder.unpackedLines.length;

export const formatQuantity = (quantity: number): string =>
  quantity.toLocaleString('nl-NL', { maximumFractionDigits: 3 });

export const formatAddressOneLine = (address: DeliveryAddress): string =>
  `${address.address}, ${address.postalCode} ${address.city}`;

export const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** A row with a null value is shown as just its key */
type Row = [string, string | null];

const krampOrderNumberRow = (krampOrder: KrampOrder): Row =>
  krampOrder.krampOrderNumber
    ? ['Kramp ordernummer', krampOrder.krampOrderNumber]
    : ['Onbekend Kramp ordernummer', null];

const addressKey = (street: string, postalCode: string): string => {
  const compactStreet = street.toLowerCase().replace(/[^a-z0-9]/g, '');
  const houseNumber = /\d+[a-z]{0,4}$/.exec(compactStreet)?.[0] ?? compactStreet;
  return `${postalCode.toUpperCase().replace(/\s/g, '')} ${houseNumber}`;
};

const shipToRow = (krampOrder: KrampOrder, shipTo: KrampShippingAddress): Row => [
  SHIP_TO_NO,
  shipToDiffersFrom(krampOrder.deliveryAddress, shipTo)
    ? `${formatShipToLine(shipTo)} (wijkt af van het afleveradres)`
    : formatShipToLine(shipTo),
];

const escapedRow = ([key, value]: Row): Row => [key, value === null ? null : escapeHtml(value)];

const isHttpUrl = (url: string): boolean => /^https?:\/\//i.test(url);

const plainKeyValues = (rows: Row[]): string =>
  rows.map(([key, value]) => (value === null ? key : `${key}: ${value}`)).join('\n');

const plainKrampOrder = (
  leadingRows: Row[],
  krampOrder: KrampOrder,
  shipTo: KrampShippingAddress | null,
  nseStatuses: NseStatuses
): string => {
  const rows: Row[] = [...leadingRows, krampOrderNumberRow(krampOrder)];
  if (krampOrder.deliveryAddress) {
    rows.push(['Afleveradres', formatAddressOneLine(krampOrder.deliveryAddress)]);
  }
  if (shipTo) {
    rows.push(shipToRow(krampOrder, shipTo));
  }
  const packages = hasNoShipmentInfo(krampOrder)
    ? [NO_SHIPMENT_INFO]
    : krampOrder.packages.map(shipmentPackage => plainPackage(shipmentPackage, nseStatuses));
  const unpacked = krampOrder.unpackedLines.length ? [plainUnpackedLines(krampOrder.unpackedLines)] : [];
  return [plainKeyValues(rows), ...packages, ...unpacked].join('\n\n');
};

const plainPackage = (shipmentPackage: ShipmentPackage, nseStatuses: NseStatuses): string => {
  const lines = [`Pakket: ${shipmentPackage.barcode} (${carrierName(shipmentPackage.carrier)})`];
  if (shipmentPackage.trackingUrl) {
    lines.push(`Track & trace: ${shipmentPackage.trackingUrl}`);
  }
  const status = packageStatus(shipmentPackage, nseStatuses);
  if (status) {
    lines.push(`Status: ${status}`);
  }
  lines.push(...shipmentPackage.contents.map(plainPackageLine));
  return lines.join('\n');
};

const plainPackageLine = (line: PackageLine): string =>
  `- ${formatQuantity(line.quantity)} x ${line.sku} ${line.description}`;

const plainUnpackedLines = (lines: UnpackedLine[]): string =>
  [
    `${UNPACKED_LINES}:`,
    ...lines.map(
      line => `- ${formatQuantity(line.quantity)} x ${line.sku} ${line.description}: ${unpackedLineStatus(line)}`
    ),
  ].join('\n');

/** Values must already be escaped, so callers can pass markup such as <br> */
const htmlKeyValues = (rows: Row[]): string =>
  `<table style="${KEY_VALUE_TABLE}">` +
  rows
    .map(([key, value]) =>
      value === null
        ? `<tr><th colspan="2" style="${KEY_CELL}">${key}</th></tr>`
        : `<tr><th style="${KEY_CELL}">${key}</th><td style="${VALUE_CELL}">${value}</td></tr>`
    )
    .join('') +
  '</table>';

/** leadingRows must already be escaped */
const htmlKrampOrder = (
  leadingRows: Row[],
  krampOrder: KrampOrder,
  shipTo: KrampShippingAddress | null,
  spaceAbove: boolean,
  nseStatuses: NseStatuses
): string => {
  const rows: Row[] = [...leadingRows, escapedRow(krampOrderNumberRow(krampOrder))];
  if (krampOrder.deliveryAddress) {
    const address = krampOrder.deliveryAddress;
    rows.push([
      'Afleveradres',
      `${escapeHtml(address.address)}<br>${escapeHtml(address.postalCode)} ${escapeHtml(address.city)}`,
    ]);
  }
  if (shipTo) {
    rows.push(escapedRow(shipToRow(krampOrder, shipTo)));
  }
  const packages = hasNoShipmentInfo(krampOrder)
    ? [`<p style="${PARAGRAPH}"><em>${NO_SHIPMENT_INFO}</em></p>`]
    : krampOrder.packages.map(shipmentPackage => htmlPackage(shipmentPackage, nseStatuses));
  const unpacked = krampOrder.unpackedLines.length ? [htmlUnpackedLines(krampOrder.unpackedLines)] : [];
  const spacer = spaceAbove ? [`<p style="${SPACER}">&nbsp;</p>`] : [];
  return [...spacer, htmlKeyValues(rows), ...packages, ...unpacked].join('\n');
};

const packageStatus = (shipmentPackage: ShipmentPackage, nseStatuses: NseStatuses): string | null => {
  const nseStatus = nseStatuses.get(shipmentPackage.barcode);
  return nseStatus ? nseScanText(nseStatus) : null;
};

const htmlPackage = (shipmentPackage: ShipmentPackage, nseStatuses: NseStatuses): string => {
  const barcode = escapeHtml(shipmentPackage.barcode);
  const barcodeHtml = isHttpUrl(shipmentPackage.trackingUrl)
    ? `<a href="${escapeHtml(shipmentPackage.trackingUrl)}">${barcode}</a>`
    : barcode;
  const title = `<p style="${PARAGRAPH}"><strong>Pakket:</strong> ${barcodeHtml} (${escapeHtml(carrierName(shipmentPackage.carrier))})</p>`;
  const status = packageStatus(shipmentPackage, nseStatuses);
  const titleWithStatus = status ? `${title}\n<p style="${PARAGRAPH}">Status: ${escapeHtml(status)}</p>` : title;
  if (!shipmentPackage.contents.length) {
    return titleWithStatus;
  }
  const header =
    `<tr><th style="${CONTENTS_HEADER}">SKU</th><th style="${CONTENTS_HEADER}">Omschrijving</th>` +
    `<th style="${CONTENTS_HEADER}">Aantal</th></tr>`;
  const body = shipmentPackage.contents
    .map(
      line =>
        `<tr><td style="${CONTENTS_CELL}">${escapeHtml(line.sku)}</td>` +
        `<td style="${CONTENTS_CELL}">${escapeHtml(line.description)}</td>` +
        `<td style="${CONTENTS_CELL_NUMBER}">${formatQuantity(line.quantity)}</td></tr>`
    )
    .join('');
  return `${titleWithStatus}\n<table style="${CONTENTS_TABLE}">${header}${body}</table>`;
};

const htmlUnpackedLines = (lines: UnpackedLine[]): string => {
  const title = `<p style="${PARAGRAPH}"><strong>${UNPACKED_LINES}:</strong></p>`;
  const header =
    `<tr><th style="${CONTENTS_HEADER}">SKU</th><th style="${CONTENTS_HEADER}">Omschrijving</th>` +
    `<th style="${CONTENTS_HEADER}">Aantal</th><th style="${CONTENTS_HEADER}">Status</th></tr>`;
  const body = lines
    .map(
      line =>
        `<tr><td style="${CONTENTS_CELL}">${escapeHtml(line.sku)}</td>` +
        `<td style="${CONTENTS_CELL}">${escapeHtml(line.description)}</td>` +
        `<td style="${CONTENTS_CELL_NUMBER}">${formatQuantity(line.quantity)}</td>` +
        `<td style="${CONTENTS_CELL}">${escapeHtml(unpackedLineStatus(line))}</td></tr>`
    )
    .join('');
  return `${title}\n<table style="${CONTENTS_TABLE}">${header}${body}</table>`;
};
